/**
 * Translation table: canary `browser_*` tool calls → chrome-devtools-mcp tool calls.
 *
 * For attached (user's real Chrome) sessions we proxy each canary tool to an
 * equivalent chrome-devtools-mcp tool. Refs are opaque tokens in both worlds
 * (canary uses `eN`, chrome-devtools-mcp uses `uid_N_M`) — we pass them through
 * verbatim. The snapshot text format differs between the two but is structured
 * similarly; Claude's skill guidance explains the difference.
 *
 * Gaps (not available in attached mode) return an error string; everything
 * else maps cleanly.
 *
 * @module
 */

import type { ChromeDevtoolsMcpClient, McpToolResult } from './mcp-client.js';

export type ProxyResult =
  | { ok: true; text: string; images?: Array<{ data: string; mimeType: string }> }
  | { ok: false; error: string };

function textFromMcp(result: McpToolResult): string {
  if (!result.content) return '';
  return result.content
    .filter((c) => c.type === 'text' && typeof c.text === 'string')
    .map((c) => c.text as string)
    .join('\n');
}

function imagesFromMcp(
  result: McpToolResult,
): Array<{ data: string; mimeType: string }> | undefined {
  if (!result.content) return undefined;
  const images = result.content
    .filter((c) => c.type === 'image' && typeof c.data === 'string')
    .map((c) => ({ data: c.data as string, mimeType: c.mimeType ?? 'image/png' }));
  return images.length > 0 ? images : undefined;
}

function toResult(result: McpToolResult): ProxyResult {
  const text = textFromMcp(result);
  const images = imagesFromMcp(result);
  if (result.isError) {
    return { ok: false, error: text || 'chrome-devtools-mcp returned an error with no message' };
  }
  return { ok: true, text, images };
}

/**
 * Snapshots print refs as "uid=2_3 ..."; claude naturally copies the visible
 * form. chrome-devtools-mcp's tools want the bare value (e.g. "2_3"). Accept
 * either and normalize.
 */
function normalizeUid(ref: string | undefined): string | undefined {
  if (!ref) return ref;
  return ref.startsWith('uid=') ? ref.slice(4) : ref;
}

function notSupportedInAttachedMode(canaryTool: string): ProxyResult {
  return {
    ok: false,
    error: `${canaryTool} is not supported in attached mode (real Chrome). Use a fresh session (--credential) if you need this.`,
  };
}

type Translator = (
  mcp: ChromeDevtoolsMcpClient,
  args: Record<string, unknown>,
) => Promise<ProxyResult>;

const translators: Record<string, Translator> = {
  browser_navigate: async (mcp, args) => {
    const url = args.url as string;
    return toResult(await mcp.callTool('navigate_page', { url }));
  },

  browser_navigate_back: async (mcp) => {
    return toResult(await mcp.callTool('navigate_page', { type: 'back' }));
  },

  browser_snapshot: async (mcp, args) => {
    const mode = args.mode as 'auto' | 'both' | 'screenshot' | 'tree' | undefined;
    if (mode === 'screenshot') {
      return toResult(await mcp.callTool('take_screenshot', {}));
    }
    const snap = await mcp.callTool('take_snapshot', {});
    const snapResult = toResult(snap);
    if (!snapResult.ok) return snapResult;

    // --search: client-side filter on the snapshot text
    const search = args.search as string | undefined;
    let filteredText = snapResult.text;
    if (search) {
      const terms = search.split('|').map((t) => t.trim().toLowerCase()).filter(Boolean);
      if (terms.length > 0) {
        const lines = filteredText.split('\n');
        const matching = lines.filter((line) => {
          const low = line.toLowerCase();
          return terms.some((t) => low.includes(t));
        });
        filteredText =
          matching.length > 0
            ? `# Filtered snapshot (search=${JSON.stringify(search)}, ${matching.length} matches)\n${matching.join('\n')}`
            : `# No matches for search=${JSON.stringify(search)}\n\n${filteredText}`;
      }
    }

    if (mode === 'both' || mode === undefined) {
      const shot = await mcp.callTool('take_screenshot', {});
      const shotResult = toResult(shot);
      return {
        ok: true,
        text: filteredText,
        images: shotResult.ok ? shotResult.images : undefined,
      };
    }

    return { ok: true, text: filteredText };
  },

  browser_screenshot: async (mcp, args) => {
    return toResult(
      await mcp.callTool('take_screenshot', {
        uid: normalizeUid(args.ref as string | undefined),
        fullPage: args.fullPage as boolean | undefined,
      }),
    );
  },

  browser_evaluate: async (mcp, args) => {
    const fn = args.function as string;
    return toResult(await mcp.callTool('evaluate_script', { function: fn }));
  },

  browser_console_messages: async (mcp, args) => {
    const res = await mcp.callTool('list_console_messages', {});
    const result = toResult(res);
    if (!result.ok) return result;
    if (args.onlyErrors) {
      const filtered = result.text
        .split('\n')
        .filter((l) => /error/i.test(l))
        .join('\n');
      return { ok: true, text: filtered || '(no errors)' };
    }
    return result;
  },

  browser_network_requests: async (mcp) => {
    return toResult(await mcp.callTool('list_network_requests', {}));
  },

  browser_click: async (mcp, args) => {
    const ref = normalizeUid(args.ref as string | undefined);
    const x = args.x as number | undefined;
    const y = args.y as number | undefined;
    if (ref) {
      return toResult(
        await mcp.callTool('click', {
          uid: ref,
          dblClick: args.doubleClick as boolean | undefined,
        }),
      );
    }
    if (typeof x === 'number' && typeof y === 'number') {
      // chrome-devtools-mcp has no coord-click; fall back to evaluate.
      const dbl = args.doubleClick === true;
      const fn = `() => { const el = document.elementFromPoint(${x}, ${y}); if (!el) throw new Error('No element at (${x},${y})'); el.${dbl ? 'click(); el.click' : 'click'}(); return el.outerHTML.slice(0, 200); }`;
      return toResult(await mcp.callTool('evaluate_script', { function: fn }));
    }
    return { ok: false, error: 'browser_click requires either ref or (x, y)' };
  },

  browser_hover: async (mcp, args) => {
    return toResult(await mcp.callTool('hover', { uid: normalizeUid(args.ref as string) }));
  },

  browser_drag: async (mcp, args) => {
    const startRef = normalizeUid(args.startRef as string | undefined);
    const endRef = normalizeUid(args.endRef as string | undefined);
    if (!startRef || !endRef) {
      return {
        ok: false,
        error: 'browser_drag in attached mode requires startRef and endRef (coordinate drag not supported).',
      };
    }
    return toResult(await mcp.callTool('drag', { from_uid: startRef, to_uid: endRef }));
  },

  browser_type: async (mcp, args) => {
    const ref = normalizeUid(args.ref as string | undefined);
    const text = args.text as string;
    const submit = args.submit as boolean | undefined;
    if (ref) {
      const fill = await mcp.callTool('fill', { uid: ref, value: text });
      const fillResult = toResult(fill);
      if (!fillResult.ok || !submit) return fillResult;
      const press = await mcp.callTool('press_key', { key: 'Enter' });
      const pressResult = toResult(press);
      if (!pressResult.ok) return pressResult;
      return { ok: true, text: `${fillResult.text}\n${pressResult.text}`.trim() };
    }
    // No ref: type into whatever's focused
    const typed = await mcp.callTool('type_text', {
      text,
      submitKey: submit ? 'Enter' : undefined,
    });
    return toResult(typed);
  },

  browser_press_key: async (mcp, args) => {
    return toResult(await mcp.callTool('press_key', { key: args.key as string }));
  },

  browser_fill_form: async (mcp, args) => {
    const fields = args.fields as Array<{ ref: string; element?: string; value: string }>;
    if (!Array.isArray(fields)) return { ok: false, error: 'browser_fill_form requires fields[]' };
    const elements = fields.map((f) => ({ uid: normalizeUid(f.ref), value: f.value }));
    return toResult(await mcp.callTool('fill_form', { elements }));
  },

  browser_select_option: async (mcp, args) => {
    // chrome-devtools-mcp uses `fill` for <select> too.
    return toResult(
      await mcp.callTool('fill', { uid: normalizeUid(args.ref as string), value: args.value as string }),
    );
  },

  browser_file_upload: async (mcp, args) => {
    const paths = args.paths as string[] | undefined;
    const ref = normalizeUid(args.ref as string | undefined);
    if (!paths || paths.length === 0 || !ref) {
      return { ok: false, error: 'browser_file_upload in attached mode requires ref and a single path' };
    }
    return toResult(await mcp.callTool('upload_file', { uid: ref, filePath: paths[0] }));
  },

  browser_scroll: async (mcp, args) => {
    const direction = args.direction as 'up' | 'down' | 'left' | 'right';
    const amount = (args.amount as number | undefined) ?? 3;
    // amount is the canary "intensity" (1-10); map to ~300px per unit.
    const pixels = amount * 300;
    let dx = 0;
    let dy = 0;
    if (direction === 'down') dy = pixels;
    else if (direction === 'up') dy = -pixels;
    else if (direction === 'right') dx = pixels;
    else if (direction === 'left') dx = -pixels;
    const fn = `() => { window.scrollBy(${dx}, ${dy}); return { scrollX: window.scrollX, scrollY: window.scrollY }; }`;
    return toResult(await mcp.callTool('evaluate_script', { function: fn }));
  },

  browser_handle_dialog: async (mcp, args) => {
    return toResult(
      await mcp.callTool('handle_dialog', {
        action: args.action as 'accept' | 'dismiss',
        promptText: args.promptText as string | undefined,
      }),
    );
  },

  browser_dismiss_overlay: async (mcp) => {
    // No direct chrome-devtools tool; press Escape as a best-effort fallback.
    return toResult(await mcp.callTool('press_key', { key: 'Escape' }));
  },

  browser_wait_for: async (mcp, args) => {
    const text = args.text as string | undefined;
    const timeSec = args.timeSec as number | undefined;
    const timeout = args.timeout as number | undefined;
    if (text) {
      return toResult(await mcp.callTool('wait_for', { text: [text], timeout }));
    }
    if (typeof timeSec === 'number') {
      const fn = `async () => { await new Promise(r => setTimeout(r, ${Math.round(timeSec * 1000)})); return 'waited ${timeSec}s'; }`;
      return toResult(await mcp.callTool('evaluate_script', { function: fn }));
    }
    return { ok: false, error: 'browser_wait_for in attached mode requires text or time' };
  },

  browser_close: async () => {
    // Never close the user's real Chrome tab — just a no-op success.
    return { ok: true, text: 'browser_close is a no-op in attached mode (the user owns the browser).' };
  },

  browser_resize: async (mcp, args) => {
    return toResult(
      await mcp.callTool('resize_page', {
        width: args.width as number,
        height: args.height as number,
      }),
    );
  },

  browser_tabs: async (mcp, args) => {
    const action = args.action as 'list' | 'new' | 'close' | 'select';
    const index = args.index as number | undefined;
    switch (action) {
      case 'list':
        return toResult(await mcp.callTool('list_pages', {}));
      case 'new':
        return toResult(await mcp.callTool('new_page', { url: 'about:blank' }));
      case 'close':
        if (typeof index !== 'number')
          return { ok: false, error: 'browser_tabs close requires an index' };
        return toResult(await mcp.callTool('close_page', { pageId: index }));
      case 'select':
        if (typeof index !== 'number')
          return { ok: false, error: 'browser_tabs select requires an index' };
        return toResult(await mcp.callTool('select_page', { pageId: index, bringToFront: true }));
      default:
        return { ok: false, error: `browser_tabs: unknown action "${String(action)}"` };
    }
  },

  browser_list_downloads: async () => notSupportedInAttachedMode('browser_list_downloads'),
  browser_read_download: async () => notSupportedInAttachedMode('browser_read_download'),
  browser_pdf_read: async () => notSupportedInAttachedMode('browser_pdf_read'),
};

export async function translateAndCall(
  mcp: ChromeDevtoolsMcpClient,
  toolName: string,
  args: Record<string, unknown>,
): Promise<ProxyResult> {
  const translator = translators[toolName];
  if (!translator) {
    return { ok: false, error: `Unknown browser tool: ${toolName}` };
  }
  try {
    return await translator(mcp, args);
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}
